import { UserData } from '@/models/user-data.model';
import { OfficesService } from '@/services/api/franchises.service';
import { UsersService } from '@/services/api/users.service';
import { AppAuthService } from '@/services/app/auth.service';
import { DebugService } from '@/services/app/debug.service';
import { ShortcutHandlerService } from '@/services/app/shortcut-handler.service';
import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { Subscription } from 'rxjs';
import { Franchise } from 'src/app/models/franchise.model';

@Component({
    selector: 'app-general-settings',
    imports: [CommonModule, SelectModule, SelectButtonModule, FormsModule],
    template: `
    <div class="grid formgrid p-fluid">
    <div class="field mb-4 col-12 md:col-6">
        <label for="company_name" class="font-medium text-900">Default Office</label>
        <p-select [options]="offices" optionLabel="CityCode" optionValue="FID"
            [(ngModel)]="formValues.defaultFID" (onChange)="onOfficeChange($event.value)"
            placeholder="Select an Office" />
    </div>
    <div class="field mb-4 col-12 md:col-6">
        <label class="font-medium text-900" *ngIf="debugService.debugVisible()">Debug Mode -
            {{debugService.selectedWarning()}}</label>
        <p-selectButton *ngIf="debugService.debugVisible()" [options]="stateOptions" id="travel" optionLabel="label"
            optionValue="value" [ngModel]="debugService.debugMode()" (onChange)="onChangeDebugMode($event.value)" />
    </div>
    <div class="surface-border border-top-1 opacity-50 mb-4 col-12 ml-2 mr-2"></div>
    <div class="field mb-4 col-6 md:col-4">
        <label for="format" class="font-medium text-900">PDF Format</label>
        <p-selectButton [options]="formatOptions" id="format" optionLabel="label" optionValue="value"
            [ngModel]="formValues.PrintFormat" (onChange)="onFormatChange($event.value)" />
    </div>
    <div class="field mb-4 col-6 md:col-4">
        <label class="font-medium text-900">Show Travel Rate</label>
        <p-selectButton [options]="stateOptions" id="travel" optionLabel="label" optionValue="value"
            [ngModel]="formValues.ShowTravel" (onChange)="onTravelChange($event.value)" />
    </div>
    <div class="field mb-4 col-6 md:col-4">
        <label for="option" class="font-medium text-900">Show Option Pricing On Contract</label>
        <p-selectButton [options]="stateOptions" id="option" optionLabel="label" optionValue="value"
            [ngModel]="formValues.ShowPrice" (onChange)="onPriceChange($event.value)" />
    </div>
    <div class="field mb-4 col-6 md:col-4">
        <label for="standards" class="font-medium text-900">Contract Standards say "Included"</label>
        <p-selectButton [options]="stateOptions" id="standards" optionLabel="label" optionValue="value"
            [ngModel]="formValues.StandardIncluded" (onChange)="onStandardChange($event.value)" />
    </div>
    <div class="surface-border border-top-1 opacity-50 mb-4 col-12"></div>
</div>
    `,
    standalone: true,
})
export class GeneralSettingsComponent {
  profileService = inject(AppAuthService);
  usersService = inject(UsersService);
  officeService = inject(OfficesService);
  shortcutHandlerService = inject(ShortcutHandlerService);
  debugService = inject(DebugService);
  formValues = {
    ShowTravel: false,
    ShowPrice: false,
    StandardIncluded: false,
    PrintFormat: 'Letter',
    defaultFID: 0
  };
  selectedTab: string = 'profile';
  largeImage: string = '';
  selectedOffice: Franchise | null = null;
  initialValuesSet: boolean = false;
  subscriptions: Subscription[] = [];
  stateOptions: any[] = [{ label: 'On', value: true },{ label: 'Off', value: false }];
  formatOptions: any[] = [{ label: 'Letter', value: 'Letter' },{ label: 'Legal', value: 'Legal' }];

  subscriptionArray: Subscription[] = [];

  constructor(
    
  ) {
    effect(() => {
      const profile = this.profileService.userData();
      if(profile) {
        this.setInitialValues(profile);
      }
    });
    this.shortcutHandlerService.registerShortcut('ctrl+q', () => {
      this.handleDebugKey();
    });

    if(this.officeService.offices().length === 0) {
      this.officeService.fetchOffices();
    }
  }

  get profileData() {
    return this.profileService.userData();
  }

  get offices() {
    return this.officeService.getFilteredOffices('Binders', 'edit');
  }

  get officeOptions():{ label: string; value: number }[] {
    return this.offices.map(office => ({
      label: office.Title,
      value: office.FID
    }));
  }


  setInitialValues(profileData: UserData) {
    this.formValues = {
      ShowTravel: profileData?.ShowTravel || false,
      ShowPrice: profileData ?.ShowPrice || false,
      StandardIncluded: profileData?.StandardIncluded || false,
      PrintFormat: profileData?.PrintFormat || 'Letter',
      defaultFID: profileData?.defaultFID || 0
    };
    this.selectOffice(this.formValues.defaultFID);
    this.initialValuesSet = true;
  }

  onOfficeChange(event: any) {
    const selectedFID = event.value;
    this.selectOffice(selectedFID);
    if (this.profileData) {
      this.profileService.updateUserData(this.profileData.UID, { defaultFID: event.value });
    }
  }

  selectOffice(fid: number) {
    console.log('office service triggered:', fid);
    const office = this.offices.find(office => office.FID === fid);
    if (office) {
      console.log('office selected:', office);
      this.selectedOffice = office;
    }
  }

  handleDebugKey() {
    this.debugService.toggleDebugVisible();
  }

  onFormatChange(newValue: any) {
    console.log('format changed:', newValue);
    if (this.profileData && newValue !== this.profileData.PrintFormat) {
      this.profileService.updateUserData(this.profileData.UID, { PrintFormat: newValue });
    }
  }
  
  onTravelChange(newValue: any) {
    console.log('travel changed:', newValue);
    if (this.profileData) {
      this.profileService.updateUserData(this.profileData.UID, { ShowTravel: newValue });
    }
  }
  
  onPriceChange(newValue: any) {
    console.log('price changed:', newValue);
    if (this.profileData) {
      this.profileService.updateUserData(this.profileData.UID, { ShowPrice: newValue });
    }
  }

  onChangeDebugMode(newValue: any) {
    this.debugService.setDebugMode(newValue);
  }
  
  onStandardChange(newValue: any) {
    console.log('standard changed:', newValue);
    if (this.profileData) {
      this.profileService.updateUserData(this.profileData.UID, { StandardIncluded: newValue });
    }
  }

  ngOnDestroy() {
    this.subscriptionArray.forEach(subscription => subscription.unsubscribe());
  }
}
